/* platform/featureRegistry — Canonical feature key registry (Architecture Section 4.3).
   Domains: platform, crm, ai, channels, analytics, org, developer, storage.
   Leaf keys are stored in Plan.featureKeys. Parent key enables all children.

   BACKWARD COMPAT (FOUNDATION-003, 2026-07-01):
   All keys present in the seeded plan DB migrations are preserved — they are now
   either leaf keys or parent keys whose children cover the same capabilities.
   hasFeature() resolves parent keys to their children via prefix-walk, so existing
   DB rows (with old parent shorthands like 'channels.email', 'org.branding') continue
   to grant access to all child leaf keys without a DB migration. */

export const FEATURE_REGISTRY = [

  /* ── PLATFORM (Super-Admin console — not customer-facing) ──────────────── */
  {
    key: 'platform', label: 'Platform Console', icon: 'Globe',
    children: [
      {
        key: 'platform.organizations', label: 'Organizations', icon: 'Building2',
        children: [
          { key: 'platform.organizations.view',    label: 'View'    },
          { key: 'platform.organizations.create',  label: 'Create'  },
          { key: 'platform.organizations.edit',    label: 'Edit'    },
          { key: 'platform.organizations.suspend', label: 'Suspend' },
          { key: 'platform.organizations.delete',  label: 'Delete'  },
        ],
      },
      {
        key: 'platform.plans', label: 'Plans', icon: 'Package',
        children: [
          { key: 'platform.plans.view',    label: 'View'    },
          { key: 'platform.plans.create',  label: 'Create'  },
          { key: 'platform.plans.edit',    label: 'Edit'    },
          { key: 'platform.plans.archive', label: 'Archive' },
        ],
      },
      {
        key: 'platform.subscriptions', label: 'Subscriptions', icon: 'CreditCard',
        children: [
          { key: 'platform.subscriptions.view',   label: 'View'   },
          { key: 'platform.subscriptions.manage', label: 'Manage' },
        ],
      },
      {
        key: 'platform.users', label: 'Platform Users', icon: 'Users',
        children: [
          { key: 'platform.users.view',    label: 'View'    },
          { key: 'platform.users.invite',  label: 'Invite'  },
          { key: 'platform.users.edit',    label: 'Edit'    },
          { key: 'platform.users.suspend', label: 'Suspend' },
          { key: 'platform.users.delete',  label: 'Delete'  },
        ],
      },
      {
        key: 'platform.partners', label: 'Partners', icon: 'Handshake',
        children: [
          { key: 'platform.partners.view',   label: 'View'   },
          { key: 'platform.partners.create', label: 'Create' },
          { key: 'platform.partners.edit',   label: 'Edit'   },
        ],
      },
      {
        key: 'platform.analytics', label: 'Platform Analytics', icon: 'BarChart2',
        children: [
          { key: 'platform.analytics.view',   label: 'View'   },
          { key: 'platform.analytics.export', label: 'Export' },
        ],
      },
      {
        key: 'platform.audit', label: 'Audit Logs', icon: 'ClipboardList',
        children: [
          { key: 'platform.audit.view',   label: 'View'   },
          { key: 'platform.audit.export', label: 'Export' },
        ],
      },
      {
        key: 'platform.licenses', label: 'Licenses', icon: 'Award',
        children: [
          { key: 'platform.licenses.view',   label: 'View'   },
          { key: 'platform.licenses.create', label: 'Create' },
          { key: 'platform.licenses.manage', label: 'Manage' },
        ],
      },
      {
        key: 'platform.health', label: 'System Health', icon: 'Activity',
        children: [
          { key: 'platform.health.view', label: 'View' },
        ],
      },
      {
        key: 'platform.backup', label: 'Platform Backup', icon: 'Archive',
        children: [
          { key: 'platform.backup.manage', label: 'Manage' },
        ],
      },
      {
        key: 'platform.settings', label: 'Platform Settings', icon: 'Settings',
        children: [
          { key: 'platform.settings.view', label: 'View' },
          { key: 'platform.settings.edit', label: 'Edit' },
        ],
      },
    ],
  },

  /* ── CRM (Workspace tier) ───────────────────────────────────────────────── */
  {
    key: 'crm', label: 'CRM', icon: 'Users',
    children: [
      {
        key: 'crm.dashboard', label: 'Dashboard', icon: 'LayoutDashboard',
        children: [
          { key: 'crm.dashboard.view', label: 'View' },
        ],
      },
      {
        key: 'crm.people', label: 'People', icon: 'Users',
        children: [
          { key: 'crm.people.view',        label: 'View'        },
          { key: 'crm.people.create',      label: 'Create'      },
          { key: 'crm.people.edit',        label: 'Edit'        },
          { key: 'crm.people.delete',      label: 'Delete'      },
          { key: 'crm.people.import',      label: 'Import'      },
          { key: 'crm.people.export',      label: 'Export'      },
          { key: 'crm.people.merge',       label: 'Merge'       },
          { key: 'crm.people.bulk-update', label: 'Bulk Update' },
          { key: 'crm.people.bulk-delete', label: 'Bulk Delete' },
        ],
      },
      {
        key: 'crm.companies', label: 'Companies', icon: 'Building2',
        children: [
          { key: 'crm.companies.view',        label: 'View'        },
          { key: 'crm.companies.create',      label: 'Create'      },
          { key: 'crm.companies.edit',        label: 'Edit'        },
          { key: 'crm.companies.delete',      label: 'Delete'      },
          { key: 'crm.companies.import',      label: 'Import'      },
          { key: 'crm.companies.export',      label: 'Export'      },
          { key: 'crm.companies.bulk-update', label: 'Bulk Update' },
          { key: 'crm.companies.bulk-delete', label: 'Bulk Delete' },
        ],
      },
      {
        key: 'crm.deals', label: 'Deals', icon: 'TrendingUp',
        children: [
          { key: 'crm.deals.view',            label: 'View'                },
          { key: 'crm.deals.create',          label: 'Create'              },
          { key: 'crm.deals.edit',            label: 'Edit'                },
          { key: 'crm.deals.delete',          label: 'Delete'              },
          { key: 'crm.deals.pipeline.manage', label: 'Pipeline Management' },
          { key: 'crm.deals.forecast',        label: 'Forecast'            },
          { key: 'crm.deals.export',          label: 'Export'              },
          { key: 'crm.deals.bulk-update',     label: 'Bulk Update'         },
          { key: 'crm.deals.bulk-delete',     label: 'Bulk Delete'         },
        ],
      },
      {
        key: 'crm.pipeline', label: 'Pipeline View', icon: 'Columns',
        children: [
          { key: 'crm.pipeline.view', label: 'View' },
        ],
      },
      {
        key: 'crm.tasks', label: 'Tasks', icon: 'CheckSquare',
        children: [
          { key: 'crm.tasks.view',     label: 'View'     },
          { key: 'crm.tasks.create',   label: 'Create'   },
          { key: 'crm.tasks.edit',     label: 'Edit'     },
          { key: 'crm.tasks.delete',   label: 'Delete'   },
          { key: 'crm.tasks.assign',   label: 'Assign'   },
          { key: 'crm.tasks.calendar', label: 'Calendar' },
        ],
      },
      {
        key: 'crm.calendar', label: 'Calendar', icon: 'Calendar',
        children: [
          { key: 'crm.calendar.view', label: 'View' },
        ],
      },
      {
        key: 'crm.notes', label: 'Notes', icon: 'FileText',
        children: [
          { key: 'crm.notes.view',   label: 'View'   },
          { key: 'crm.notes.create', label: 'Create' },
          { key: 'crm.notes.edit',   label: 'Edit'   },
          { key: 'crm.notes.delete', label: 'Delete' },
        ],
      },
      {
        key: 'crm.activities', label: 'Activities', icon: 'Activity',
        children: [
          { key: 'crm.activities.view',   label: 'View'   },
          { key: 'crm.activities.log',    label: 'Log'    },
          { key: 'crm.activities.edit',   label: 'Edit'   },
          { key: 'crm.activities.delete', label: 'Delete' },
        ],
      },
      {
        key: 'crm.documents', label: 'Documents', icon: 'Folder',
        children: [
          { key: 'crm.documents.view',   label: 'View'   },
          { key: 'crm.documents.upload', label: 'Upload' },
          { key: 'crm.documents.delete', label: 'Delete' },
        ],
      },
      {
        key: 'crm.saved-views', label: 'Saved Views', icon: 'BookMarked',
        children: [
          { key: 'crm.saved-views.view',   label: 'View'   },
          { key: 'crm.saved-views.create', label: 'Create' },
          { key: 'crm.saved-views.manage', label: 'Manage' },
        ],
      },
    ],
  },

  /* ── AI PLATFORM ────────────────────────────────────────────────────────── */
  {
    key: 'ai', label: 'AI Platform', icon: 'Cpu',
    children: [
      {
        key: 'ai.copilot', label: 'Copilot', icon: 'Bot',
        children: [
          { key: 'ai.copilot.use', label: 'Use Copilot' },
        ],
      },
      {
        key: 'ai.email-assist', label: 'Email Assist', icon: 'Mail',
        children: [
          { key: 'ai.email-assist.use', label: 'Use' },
        ],
      },
      {
        key: 'ai.agents', label: 'AI Agents', icon: 'Cpu',
        children: [
          { key: 'ai.agents.create',    label: 'Create'    },
          { key: 'ai.agents.configure', label: 'Configure' },
          { key: 'ai.agents.deploy',    label: 'Deploy'    },
        ],
      },
      {
        key: 'ai.knowledge-base', label: 'Knowledge Base', icon: 'BookOpen',
        children: [
          { key: 'ai.knowledge-base.create', label: 'Create' },
          { key: 'ai.knowledge-base.manage', label: 'Manage' },
        ],
      },
      {
        key: 'ai.prompts', label: 'Prompt Library', icon: 'MessageSquare',
        children: [
          { key: 'ai.prompts.create', label: 'Create' },
          { key: 'ai.prompts.manage', label: 'Manage' },
        ],
      },
      {
        key: 'ai.models', label: 'AI Models', icon: 'Settings',
        children: [
          { key: 'ai.models.configure', label: 'Configure' },
        ],
      },
    ],
  },

  /* ── CHANNELS ───────────────────────────────────────────────────────────── */
  {
    key: 'channels', label: 'Channels', icon: 'MessageSquare',
    children: [
      {
        key: 'channels.email', label: 'Email', icon: 'Mail',
        children: [
          { key: 'channels.email.use',       label: 'Use'       },
          { key: 'channels.email.configure', label: 'Configure' },
        ],
      },
      {
        key: 'channels.whatsapp', label: 'WhatsApp', icon: 'MessageSquare',
        children: [
          { key: 'channels.whatsapp.use',       label: 'Use'       },
          { key: 'channels.whatsapp.configure', label: 'Configure' },
        ],
      },
      {
        key: 'channels.sms', label: 'SMS', icon: 'MessageSquare',
        children: [
          { key: 'channels.sms.use',       label: 'Use'       },
          { key: 'channels.sms.configure', label: 'Configure' },
        ],
      },
      {
        key: 'channels.voice', label: 'Voice', icon: 'Phone',
        children: [
          { key: 'channels.voice.use',       label: 'Use'       },
          { key: 'channels.voice.configure', label: 'Configure' },
        ],
      },
      {
        key: 'channels.webchat', label: 'Web Chat', icon: 'Globe',
        children: [
          { key: 'channels.webchat.use',       label: 'Use'       },
          { key: 'channels.webchat.configure', label: 'Configure' },
        ],
      },
      {
        key: 'channels.social', label: 'Social', icon: 'Share2',
        children: [
          {
            key: 'channels.social.facebook', label: 'Facebook', icon: 'Share2',
            children: [
              { key: 'channels.social.facebook.use',       label: 'Use'       },
              { key: 'channels.social.facebook.configure', label: 'Configure' },
            ],
          },
          {
            key: 'channels.social.instagram', label: 'Instagram', icon: 'Share2',
            children: [
              { key: 'channels.social.instagram.use',       label: 'Use'       },
              { key: 'channels.social.instagram.configure', label: 'Configure' },
            ],
          },
          {
            key: 'channels.social.telegram', label: 'Telegram', icon: 'Send',
            children: [
              { key: 'channels.social.telegram.use',       label: 'Use'       },
              { key: 'channels.social.telegram.configure', label: 'Configure' },
            ],
          },
        ],
      },
      {
        key: 'channels.slack', label: 'Slack', icon: 'Hash',
        children: [
          { key: 'channels.slack.use',       label: 'Use'       },
          { key: 'channels.slack.configure', label: 'Configure' },
        ],
      },
    ],
  },

  /* ── ANALYTICS ──────────────────────────────────────────────────────────── */
  {
    key: 'analytics', label: 'Analytics', icon: 'BarChart2',
    children: [
      {
        key: 'analytics.crm', label: 'CRM Analytics', icon: 'BarChart2',
        children: [
          { key: 'analytics.crm.basic',    label: 'Basic Reports'     },
          { key: 'analytics.crm.advanced', label: 'Advanced Reports'  },
          { key: 'analytics.crm.revenue',  label: 'Revenue Analytics' },
        ],
      },
      {
        key: 'analytics.workspace', label: 'Workspace Analytics', icon: 'Activity',
        children: [
          { key: 'analytics.workspace.view',   label: 'View'   },
          { key: 'analytics.workspace.export', label: 'Export' },
        ],
      },
      {
        key: 'analytics.org', label: 'Org Analytics', icon: 'Users',
        children: [
          { key: 'analytics.org.users',  label: 'User Analytics' },
          { key: 'analytics.org.export', label: 'Export'         },
        ],
      },
      {
        key: 'analytics.platform', label: 'Platform Analytics', icon: 'Globe',
        children: [
          { key: 'analytics.platform.view',   label: 'View'   },
          { key: 'analytics.platform.export', label: 'Export' },
        ],
      },
    ],
  },

  /* ── ORGANIZATION (Org-console tier) ────────────────────────────────────── */
  {
    key: 'org', label: 'Organization', icon: 'Building2',
    children: [
      {
        key: 'org.workspaces', label: 'Workspaces', icon: 'Layout',
        children: [
          { key: 'org.workspaces.view',   label: 'View'   },
          { key: 'org.workspaces.create', label: 'Create' },
          { key: 'org.workspaces.edit',   label: 'Edit'   },
          { key: 'org.workspaces.delete', label: 'Delete' },
        ],
      },
      {
        key: 'org.members', label: 'Members', icon: 'Users',
        children: [
          { key: 'org.members.view',   label: 'View'   },
          { key: 'org.members.invite', label: 'Invite' },
          { key: 'org.members.edit',   label: 'Edit'   },
          { key: 'org.members.remove', label: 'Remove' },
        ],
      },
      {
        key: 'org.branding', label: 'Branding', icon: 'Eye',
        children: [
          { key: 'org.branding.view', label: 'View' },
          { key: 'org.branding.edit', label: 'Edit' },
        ],
      },
      {
        key: 'org.whitelabel', label: 'White Label', icon: 'Globe',
        children: [
          { key: 'org.whitelabel.configure', label: 'Configure' },
        ],
      },
      {
        key: 'org.custom-fields', label: 'Custom Fields', icon: 'Sliders',
        children: [
          { key: 'org.custom-fields.view',   label: 'View'   },
          { key: 'org.custom-fields.create', label: 'Create' },
          { key: 'org.custom-fields.edit',   label: 'Edit'   },
          { key: 'org.custom-fields.delete', label: 'Delete' },
        ],
      },
      {
        key: 'org.teams', label: 'Teams', icon: 'UsersRound',
        children: [
          { key: 'org.teams.view',   label: 'View'   },
          { key: 'org.teams.create', label: 'Create' },
          { key: 'org.teams.edit',   label: 'Edit'   },
          { key: 'org.teams.delete', label: 'Delete' },
        ],
      },
      {
        key: 'org.roles', label: 'Roles & Permissions', icon: 'Shield',
        children: [
          { key: 'org.roles.view',   label: 'View'   },
          { key: 'org.roles.create', label: 'Create' },
          { key: 'org.roles.edit',   label: 'Edit'   },
          { key: 'org.roles.delete', label: 'Delete' },
        ],
      },
      {
        key: 'org.security', label: 'Security Center', icon: 'ShieldCheck',
        children: [
          { key: 'org.security.view',      label: 'View'      },
          { key: 'org.security.configure', label: 'Configure' },
        ],
      },
      {
        key: 'org.templates', label: 'Workspace Templates', icon: 'Layout',
        children: [
          { key: 'org.templates.view',   label: 'View'   },
          { key: 'org.templates.create', label: 'Create' },
          { key: 'org.templates.edit',   label: 'Edit'   },
          { key: 'org.templates.delete', label: 'Delete' },
        ],
      },
      {
        key: 'org.audit', label: 'Audit Logs', icon: 'ClipboardList',
        children: [
          { key: 'org.audit.view',   label: 'View'   },
          { key: 'org.audit.export', label: 'Export' },
        ],
      },
      {
        key: 'org.backup', label: 'Backup & Restore', icon: 'Archive',
        children: [
          { key: 'org.backup.view',    label: 'View'    },
          { key: 'org.backup.create',  label: 'Create'  },
          { key: 'org.backup.restore', label: 'Restore' },
        ],
      },
      {
        key: 'org.licenses', label: 'Licenses', icon: 'Award',
        children: [
          { key: 'org.licenses.view', label: 'View' },
        ],
      },
      {
        key: 'org.analytics', label: 'Org Analytics', icon: 'BarChart2',
        children: [
          { key: 'org.analytics.view',   label: 'View'   },
          { key: 'org.analytics.export', label: 'Export' },
        ],
      },
      {
        key: 'org.settings', label: 'Settings', icon: 'Settings',
        children: [
          { key: 'org.settings.view', label: 'View' },
          { key: 'org.settings.edit', label: 'Edit' },
        ],
      },
      {
        key: 'org.usage', label: 'Usage & Quotas', icon: 'Gauge',
        children: [
          { key: 'org.usage.view', label: 'View' },
        ],
      },
      {
        key: 'org.health', label: 'Health Center', icon: 'Activity',
        children: [
          { key: 'org.health.view', label: 'View' },
        ],
      },
      {
        key: 'org.search', label: 'Global Search', icon: 'Search',
        children: [
          { key: 'org.search.use', label: 'Use' },
        ],
      },
      {
        key: 'org.support', label: 'Support Center', icon: 'HelpCircle',
        children: [
          { key: 'org.support.view',   label: 'View'   },
          { key: 'org.support.create', label: 'Create' },
        ],
      },
      {
        key: 'org.notifications', label: 'Notifications', icon: 'Bell',
        children: [
          { key: 'org.notifications.view',      label: 'View'      },
          { key: 'org.notifications.configure', label: 'Configure' },
        ],
      },
      {
        key: 'org.integrations', label: 'Integrations', icon: 'Zap',
        children: [
          { key: 'org.integrations.view',      label: 'View'      },
          { key: 'org.integrations.configure', label: 'Configure' },
        ],
      },
      {
        key: 'org.insights', label: 'Org Insights', icon: 'TrendingUp',
        children: [
          { key: 'org.insights.view', label: 'View' },
        ],
      },
    ],
  },

  /* ── DEVELOPER ──────────────────────────────────────────────────────────── */
  {
    key: 'developer', label: 'Developer', icon: 'Code',
    children: [
      {
        key: 'developer.api-keys', label: 'API Keys', icon: 'Key',
        children: [
          { key: 'developer.api-keys.view',   label: 'View'   },
          { key: 'developer.api-keys.create', label: 'Create' },
          { key: 'developer.api-keys.delete', label: 'Delete' },
        ],
      },
      {
        key: 'developer.webhooks', label: 'Webhooks', icon: 'Webhook',
        children: [
          { key: 'developer.webhooks.view',   label: 'View'   },
          { key: 'developer.webhooks.create', label: 'Create' },
          { key: 'developer.webhooks.edit',   label: 'Edit'   },
          { key: 'developer.webhooks.delete', label: 'Delete' },
        ],
      },
      {
        key: 'developer.oauth-apps', label: 'OAuth Apps', icon: 'Lock',
        children: [
          { key: 'developer.oauth-apps.view',   label: 'View'   },
          { key: 'developer.oauth-apps.create', label: 'Create' },
          { key: 'developer.oauth-apps.edit',   label: 'Edit'   },
          { key: 'developer.oauth-apps.delete', label: 'Delete' },
        ],
      },
      {
        key: 'developer.sdk', label: 'SDK Access', icon: 'Code',
        children: [
          { key: 'developer.sdk.access', label: 'Access' },
        ],
      },
      {
        key: 'developer.secrets', label: 'Secrets', icon: 'EyeOff',
        children: [
          { key: 'developer.secrets.view',   label: 'View'   },
          { key: 'developer.secrets.create', label: 'Create' },
          { key: 'developer.secrets.delete', label: 'Delete' },
        ],
      },
    ],
  },

  /* ── STORAGE ────────────────────────────────────────────────────────────── */
  {
    key: 'storage', label: 'Storage', icon: 'HardDrive',
    children: [
      { key: 'storage.documents', label: 'Documents Storage', icon: 'Folder'    },
      { key: 'storage.5gb',       label: '5 GB Quota',        icon: 'HardDrive' },
      { key: 'storage.50gb',      label: '50 GB Quota',       icon: 'HardDrive' },
      { key: 'storage.500gb',     label: '500 GB Quota',      icon: 'HardDrive' },
      { key: 'storage.unlimited', label: 'Unlimited Storage', icon: 'HardDrive' },
    ],
  },
];

/* Recursively collect all leaf keys */
export function getAllLeafKeys(nodes) {
  const result = [];
  const walk = (node) => {
    if (!node.children) { result.push(node.key); return; }
    node.children.forEach(walk);
  };
  (Array.isArray(nodes) ? nodes : [nodes]).forEach(walk);
  return result;
}

export const TOTAL_FEATURE_KEYS = getAllLeafKeys(FEATURE_REGISTRY).length;

/* Check if key is enabled — supports parent key resolution.
   If 'crm.people' is in featureKeys, 'crm.people.view' is implicitly enabled.
   This means existing DB rows with pre-FOUNDATION-003 parent shorthands
   (e.g. 'channels.email', 'org.branding') continue to grant all child leaf keys. */
export function hasFeature(featureKeys, key) {
  if (!featureKeys || featureKeys.length === 0) return false;
  if (featureKeys.includes('*')) return true;
  if (featureKeys.includes(key)) return true;
  const parts = key.split('.');
  for (let i = parts.length - 1; i > 0; i--) {
    const parent = parts.slice(0, i).join('.');
    if (featureKeys.includes(parent)) return true;
  }
  return false;
}

export function hasAnyFeature(featureKeys, keys) {
  if (!featureKeys || featureKeys.length === 0) return false;
  if (featureKeys.includes('*')) return true;
  return keys.some(k => hasFeature(featureKeys, k));
}

/**
 * Compute effective feature keys for an org by applying featureOverrides on top of planFeatureKeys.
 * Mirrors FeatureResolutionService.resolve() (FOUNDATION-004A) for client-side navigation filtering.
 *
 * Resolution order (same as backend):
 *   1. plan.featureKeys  (from active/trial subscription via planFeatureKeys)
 *   2. org.featureOverrides.add   (custom additions)
 *   3. org.featureOverrides.remove  (deletions win over additions)
 *
 * @param {Object|null} org — normalized org from platform store (planFeatureKeys, featureOverrides)
 * @returns {string[]} resolved effective feature key set
 */
export function computeEffectiveFeatures(org) {
  const planKeys   = Array.isArray(org?.planFeatureKeys) ? org.planFeatureKeys : [];
  const overrides  = org?.featureOverrides || {};
  const addKeys    = Array.isArray(overrides.add)    ? overrides.add    : [];
  const removeKeys = Array.isArray(overrides.remove) ? overrides.remove : [];
  const effective  = new Set([...planKeys, ...addKeys]);
  removeKeys.forEach(k => effective.delete(k));
  return [...effective];
}

/* Normalize featureKeys to leaf-only form.
   Expands parent keys (e.g. "crm.people" → all crm.people.* leaves) and
   wildcards ("*" → all leaves). Safe to call on already-normalized arrays. */
export function normalizeFeatureKeys(keys) {
  if (!keys || keys.length === 0) return [];
  const allLeafs = getAllLeafKeys(FEATURE_REGISTRY);
  if (keys.includes('*')) return allLeafs;
  const result = new Set();
  for (const key of keys) {
    if (allLeafs.includes(key)) {
      result.add(key);
    } else {
      allLeafs.forEach(leaf => {
        if (leaf === key || leaf.startsWith(key + '.')) result.add(leaf);
      });
    }
  }
  return [...result];
}

/* Cross-domain dependencies: enabling a key auto-enables its required keys */
export const DEPENDENCY_MAP = {
  /* analytics */
  'analytics.crm.advanced':              ['analytics.crm.basic'],
  'analytics.crm.revenue':               ['analytics.crm.basic', 'analytics.crm.advanced'],
  'analytics.workspace.export':          ['analytics.workspace.view'],
  'analytics.org.export':                ['analytics.org.users'],
  'analytics.platform.export':           ['analytics.platform.view'],
  /* org */
  'org.whitelabel.configure':            ['org.branding.edit'],
  'org.backup.create':                   ['org.backup.view'],
  'org.backup.restore':                  ['org.backup.view'],
  'org.audit.export':                    ['org.audit.view'],
  /* developer */
  'developer.webhooks.create':           ['developer.api-keys.view'],
  'developer.webhooks.edit':             ['developer.api-keys.view'],
  'developer.webhooks.delete':           ['developer.api-keys.view'],
  'developer.oauth-apps.create':         ['developer.api-keys.view'],
  'developer.oauth-apps.edit':           ['developer.api-keys.view'],
  'developer.oauth-apps.delete':         ['developer.api-keys.view'],
  'developer.sdk.access':                ['developer.api-keys.view'],
  'developer.secrets.create':            ['developer.secrets.view'],
  'developer.secrets.delete':            ['developer.secrets.view'],
  /* ai */
  'ai.agents.create':                    ['ai.agents.configure'],
  'ai.agents.deploy':                    ['ai.agents.configure'],
  'ai.knowledge-base.manage':            ['ai.knowledge-base.create'],
  /* channels */
  'channels.email.configure':            ['channels.email.use'],
  'channels.whatsapp.configure':         ['channels.whatsapp.use'],
  'channels.sms.configure':              ['channels.sms.use'],
  'channels.voice.configure':            ['channels.voice.use'],
  'channels.webchat.configure':          ['channels.webchat.use'],
  'channels.slack.configure':            ['channels.slack.use'],
  'channels.social.facebook.configure':  ['channels.social.facebook.use'],
  'channels.social.instagram.configure': ['channels.social.instagram.use'],
  'channels.social.telegram.configure':  ['channels.social.telegram.use'],
  /* crm */
  'crm.saved-views.create':              ['crm.saved-views.view'],
  'crm.saved-views.manage':              ['crm.saved-views.create'],
  'crm.people.bulk-delete':              ['crm.people.bulk-update'],
  'crm.companies.bulk-delete':           ['crm.companies.bulk-update'],
  'crm.deals.bulk-delete':               ['crm.deals.bulk-update'],
};

/* Expand a set of keys to include all required dependencies */
export function resolveDependencies(keys) {
  const resolved = new Set(keys);
  let changed = true;
  while (changed) {
    changed = false;
    const snapshot = Array.from(resolved);
    for (const key of snapshot) {
      const deps = DEPENDENCY_MAP[key] ?? [];
      for (const dep of deps) {
        if (!resolved.has(dep)) { resolved.add(dep); changed = true; }
      }
    }
  }
  return [...resolved];
}

/* Plan tier defaults — Architecture Section 4.4. Used to pre-seed the
   feature tree when creating a new plan of a given tier.
   These use explicit leaf keys for precision; existing DB rows use pre-FOUNDATION-003
   parent shorthands which remain valid via hasFeature() parent-key resolution. */

const STARTER_KEYS = [
  /* crm */
  'crm.dashboard.view',
  'crm.people.view', 'crm.people.create', 'crm.people.edit', 'crm.people.delete',
  'crm.companies.view', 'crm.companies.create', 'crm.companies.edit', 'crm.companies.delete',
  'crm.deals.view', 'crm.deals.create', 'crm.deals.edit', 'crm.deals.delete',
  'crm.pipeline.view',
  'crm.tasks.view', 'crm.tasks.create', 'crm.tasks.edit', 'crm.tasks.delete', 'crm.tasks.calendar',
  'crm.calendar.view',
  'crm.notes.view', 'crm.notes.create', 'crm.notes.edit', 'crm.notes.delete',
  'crm.activities.view', 'crm.activities.log',
  'crm.documents.view', 'crm.documents.upload',
  /* analytics */
  'analytics.crm.basic',
  /* channels */
  'channels.email.use',
  /* storage */
  'storage.documents', 'storage.5gb',
  /* org basics */
  'org.workspaces.view',
  'org.members.view',
  'org.settings.view',
  'org.usage.view',
];

const PROFESSIONAL_KEYS = [
  ...STARTER_KEYS,
  /* crm */
  'crm.people.import', 'crm.people.export',
  'crm.companies.import', 'crm.companies.export',
  'crm.deals.pipeline.manage', 'crm.deals.export',
  'crm.tasks.assign',
  'crm.documents.delete',
  'crm.saved-views.view', 'crm.saved-views.create', 'crm.saved-views.manage',
  'crm.activities.edit', 'crm.activities.delete',
  /* analytics */
  'analytics.crm.advanced', 'analytics.org.users', 'analytics.workspace.view',
  /* ai */
  'ai.copilot.use', 'ai.email-assist.use',
  /* channels */
  'channels.email.configure',
  'channels.whatsapp.use', 'channels.whatsapp.configure',
  'channels.webchat.use', 'channels.webchat.configure',
  'channels.sms.use',
  /* org */
  'org.branding.view', 'org.branding.edit',
  'org.custom-fields.view', 'org.custom-fields.create', 'org.custom-fields.edit', 'org.custom-fields.delete',
  'org.teams.view', 'org.teams.create', 'org.teams.edit', 'org.teams.delete',
  'org.audit.view',
  'org.templates.view', 'org.templates.create', 'org.templates.edit', 'org.templates.delete',
  'org.members.invite', 'org.members.edit', 'org.members.remove',
  'org.workspaces.create', 'org.workspaces.edit',
  'org.settings.edit',
  'org.notifications.view',
  /* developer */
  'developer.api-keys.view', 'developer.api-keys.create', 'developer.api-keys.delete',
  'developer.webhooks.view', 'developer.webhooks.create', 'developer.webhooks.edit', 'developer.webhooks.delete',
  /* storage */
  'storage.50gb',
];

const BUSINESS_KEYS = [
  ...PROFESSIONAL_KEYS,
  /* crm */
  'crm.people.merge',
  'crm.people.bulk-update', 'crm.people.bulk-delete',
  'crm.companies.bulk-update', 'crm.companies.bulk-delete',
  'crm.deals.forecast',
  'crm.deals.bulk-update', 'crm.deals.bulk-delete',
  /* ai */
  'ai.prompts.create', 'ai.prompts.manage',
  /* analytics */
  'analytics.crm.revenue', 'analytics.workspace.export', 'analytics.org.export',
  /* channels */
  'channels.voice.use', 'channels.voice.configure',
  'channels.social.facebook.use', 'channels.social.instagram.use', 'channels.social.telegram.use',
  'channels.slack.use', 'channels.slack.configure',
  /* org */
  'org.roles.view', 'org.roles.create', 'org.roles.edit', 'org.roles.delete',
  'org.security.view', 'org.security.configure',
  'org.backup.view', 'org.backup.create', 'org.backup.restore',
  'org.integrations.view', 'org.integrations.configure',
  'org.notifications.configure',
  'org.workspaces.delete',
  /* developer */
  'developer.secrets.view', 'developer.secrets.create', 'developer.secrets.delete',
  /* storage */
  'storage.500gb',
];

const ENTERPRISE_KEYS = [
  ...BUSINESS_KEYS,
  /* ai */
  'ai.agents.create', 'ai.agents.configure', 'ai.agents.deploy',
  'ai.knowledge-base.create', 'ai.knowledge-base.manage',
  'ai.models.configure',
  /* analytics */
  'analytics.platform.view', 'analytics.platform.export',
  /* channels */
  'channels.social.facebook.configure', 'channels.social.instagram.configure', 'channels.social.telegram.configure',
  'channels.sms.configure',
  /* org */
  'org.whitelabel.configure',
  'org.licenses.view',
  'org.analytics.view', 'org.analytics.export',
  'org.health.view',
  'org.insights.view',
  'org.support.view', 'org.support.create',
  'org.search.use',
  /* developer */
  'developer.oauth-apps.view', 'developer.oauth-apps.create', 'developer.oauth-apps.edit', 'developer.oauth-apps.delete',
  'developer.sdk.access',
  /* storage */
  'storage.unlimited',
];

export const PLAN_FEATURE_DEFAULTS = {
  STARTER:      STARTER_KEYS,
  PROFESSIONAL: PROFESSIONAL_KEYS,
  BUSINESS:     BUSINESS_KEYS,
  ENTERPRISE:   ENTERPRISE_KEYS,
  CUSTOM:       [],
};
